import React, { useState, useEffect } from 'react';
import { Button } from '../design/Button';
import { Card, CardHeader, CardTitle, CardContent } from '../design/Card';
import { apiFetch } from '../../utils/api';
import toast from 'react-hot-toast';
import { Package, Trash2, Plus, RefreshCw, ArrowUpCircle, Loader2 } from 'lucide-react';
import { FunLoader } from '../common/FunLoader';
import { formatBytes } from '../../utils/format';

interface InstalledItem {
    name: string;
    size: number;
    modified: string;
}

interface InstalledContentCardProps {
    serverId: string;
    /** Backing directory: mods/ (Fabric/Forge) or plugins/ (Paper/Spigot). */
    folder: 'mods' | 'plugins';
    title: string;
    onBrowse: () => void;
    /** Bump to trigger a re-fetch (e.g. after the browser modal closes). */
    refreshSignal: number;
    /** Literal Tailwind classes (kept literal so they survive purging). */
    accentIconClass: string;
    accentButtonClass: string;
    accentBadgeClass: string;
}

/**
 * Lists the jars installed in a server's mods/ or plugins/ directory with add
 * and delete actions. Shared by the Fabric (mods) and Paper (plugins) flows so
 * the list UI is not duplicated.
 */
export const InstalledContentCard: React.FC<InstalledContentCardProps> = ({
    serverId, folder, title, onBrowse, refreshSignal,
    accentIconClass, accentButtonClass, accentBadgeClass,
}) => {
    const [items, setItems] = useState<InstalledItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [updates, setUpdates] = useState<Record<string, any>>({});
    const [checking, setChecking] = useState(false);
    const [updatingName, setUpdatingName] = useState<string | null>(null);
    const [filter, setFilter] = useState('');
    const label = folder === 'plugins' ? 'Plugin' : 'Mod';
    const filtered = filter ? items.filter((i) => i.name.toLowerCase().includes(filter.toLowerCase())) : items;

    const fetchItems = async () => {
        if (!serverId) return;
        setLoading(true);
        try {
            const res = await apiFetch(`/api/servers/${serverId}/mods?folder=${folder}`);
            if (res.ok) {
                const data = await res.json();
                setItems(data.mods || []);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchItems();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serverId, folder, refreshSignal]);

    const handleDelete = async (filename: string) => {
        if (!confirm(`${label} ${filename} löschen?`)) return;
        try {
            const res = await apiFetch(`/api/servers/${serverId}/mods/${filename}?folder=${folder}`, {
                method: 'DELETE',
            });
            if (res.ok) {
                toast.success(`${label} gelöscht`);
                fetchItems();
            } else {
                const data = await res.json().catch(() => ({}));
                toast.error(data.error || 'Löschen fehlgeschlagen');
            }
        } catch {
            toast.error('Löschen fehlgeschlagen');
        }
    };

    // Ask Modrinth (by file hash) which installed jars have a newer version.
    const checkUpdates = async () => {
        setChecking(true);
        try {
            const res = await apiFetch(`/api/servers/${serverId}/mods/updates?folder=${folder}`);
            if (res.ok) {
                const data = await res.json();
                const map: Record<string, any> = {};
                (data.updates || []).forEach((u: any) => { map[u.filename] = u; });
                setUpdates(map);
                const n = data.updates?.length || 0;
                toast.success(n > 0 ? `${n} Update(s) verfügbar` : 'Alles aktuell');
            }
        } catch {
            toast.error('Update-Prüfung fehlgeschlagen');
        } finally {
            setChecking(false);
        }
    };

    // Download the newer version, then remove the old jar if its name changed.
    const handleUpdate = async (item: InstalledItem) => {
        const u = updates[item.name];
        if (!u) return;
        setUpdatingName(item.name);
        try {
            const inst = await apiFetch(`/api/servers/${serverId}/mods`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ url: u.newUrl, filename: u.newFilename, folder, projectId: u.projectId, versionName: u.newVersion }),
            });
            if (!inst.ok) throw new Error('install failed');
            if (u.newFilename !== item.name) {
                await apiFetch(`/api/servers/${serverId}/mods/${item.name}?folder=${folder}`, { method: 'DELETE' });
            }
            toast.success(`${label} aktualisiert: ${u.newVersion}`);
            setUpdates((prev) => { const c = { ...prev }; delete c[item.name]; return c; });
            fetchItems();
        } catch {
            toast.error('Update fehlgeschlagen');
        } finally {
            setUpdatingName(null);
        }
    };

    return (
        <Card className="bg-ink border-line-strong text-white" noPadding>
            <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg flex items-center">
                    <Package className={`mr-2 ${accentIconClass}`} />
                    {title}
                    {items.length > 0 && (
                        <span className={`ml-2 text-xs font-bold px-2 py-0.5 rounded-md ${accentBadgeClass}`}>{items.length}</span>
                    )}
                </CardTitle>
                <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" className="border-line-strong text-fg hover:bg-white/5" onClick={checkUpdates} disabled={checking}>
                        {checking ? <Loader2 size={16} className="mr-2 animate-spin" /> : <RefreshCw size={16} className="mr-2" />}
                        Updates prüfen
                    </Button>
                    <Button size="sm" variant="outline" className={accentButtonClass} onClick={onBrowse}>
                        <Plus size={16} className="mr-2" />
                        {label} Hinzufügen
                    </Button>
                </div>
            </CardHeader>
            <CardContent>
                {loading ? (
                    <div className="p-4 text-center"><FunLoader size="sm" showProgress={false} /></div>
                ) : items.length === 0 ? (
                    <div className="text-center py-8 text-fg-muted border border-dashed border-line-strong rounded-xl">
                        Noch keine {label}s installiert.
                    </div>
                ) : (
                    <div className="space-y-2">
                        {items.length > 8 && (
                            <input
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                                placeholder={`${items.length} ${label}s durchsuchen...`}
                                className="w-full bg-black/30 border border-line rounded-lg px-3 py-2 text-sm text-white placeholder-fg-muted focus:outline-none focus:border-ember/50 mb-1"
                            />
                        )}
                        {filtered.length === 0 && (
                            <div className="text-center py-6 text-fg-muted text-sm">Kein {label} passt zu "{filter}".</div>
                        )}
                        {filtered.map((item) => (
                            <div key={item.name} className="flex items-center justify-between p-3 bg-surface-2 rounded-lg border border-line hover:border-line-strong transition-colors">
                                <div className="flex items-center gap-3">
                                    <div className={`w-8 h-8 rounded flex items-center justify-center font-bold ${accentBadgeClass}`}>
                                        jar
                                    </div>
                                    <div>
                                        <div className="font-mono text-sm flex items-center gap-2">
                                            {item.name}
                                            {updates[item.name] && (
                                                <span className="text-[10px] bg-green-500/15 text-green-400 px-2 py-0.5 rounded border border-green-500/25">
                                                    Update: {updates[item.name].newVersion}
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-xs text-fg-muted">{formatBytes(item.size)} • {new Date(item.modified).toLocaleDateString()}</div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1">
                                    {updates[item.name] && (
                                        <Button size="icon" variant="ghost" className="text-green-400 hover:text-green-300 hover:bg-green-900/20" onClick={() => handleUpdate(item)} disabled={updatingName === item.name}>
                                            {updatingName === item.name ? <Loader2 size={16} className="animate-spin" /> : <ArrowUpCircle size={16} />}
                                        </Button>
                                    )}
                                    <Button size="icon" variant="ghost" className="text-red-400 hover:text-red-300 hover:bg-red-900/20" onClick={() => handleDelete(item.name)}>
                                        <Trash2 size={16} />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    );
};
